// src/pages/CreateTournament.js
import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { auth, db } from "../firebase";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  Timestamp,
  orderBy,
  query,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const COPPER = "#B87333";

function toTimestampFromLocalDatetime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return Timestamp.fromDate(d);
}

function levelItemsFromTemplate(template) {
  const blinds = Array.isArray(template?.blinds) ? template.blinds : [];
  return blinds.filter((b) => b?.type === "level" && Number(b?.level) > 0);
}

export default function CreateTournament() {
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [blindTemplates, setBlindTemplates] = useState([]);
  const [chipTemplates, setChipTemplates] = useState([]);

  // Core fields
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [dateTime, setDateTime] = useState("");
  const [buyIn, setBuyIn] = useState("0");
  const [startingStack, setStartingStack] = useState("1500");

  // Tournament type / bounty
  const [tournamentType, setTournamentType] = useState("standard");
  const [bountyAmount, setBountyAmount] = useState("10");
  const [prizePoolContribution, setPrizePoolContribution] = useState("30");

  // Live / member-facing control
  const [communicationsLive, setCommunicationsLive] = useState(false);

  // Rebuys
  const [rebuysEnabled, setRebuysEnabled] = useState(false);
  const [rebuyCutoffLevel, setRebuyCutoffLevel] = useState("");
  const [rebuyPrice, setRebuyPrice] = useState("");
  const [rebuyPrizePoolContribution, setRebuyPrizePoolContribution] = useState("");
  const [rebuyBountyAmount, setRebuyBountyAmount] = useState("");

  // Late registration
  const [lateRegistrationEnabled, setLateRegistrationEnabled] = useState(false);
  const [lateRegistrationCutoffLevel, setLateRegistrationCutoffLevel] = useState("");

  // Add-ons
  const [addOnsEnabled, setAddOnsEnabled] = useState(false);
  const [addOnLevel, setAddOnLevel] = useState("");
  const [addOnChips, setAddOnChips] = useState("0");
  const [addOnCost, setAddOnCost] = useState("0");

  // Early bird
  const [earlyBirdEnabled, setEarlyBirdEnabled] = useState(false);
  const [earlyBirdDeadline, setEarlyBirdDeadline] = useState("");
  const [earlyBirdBonusChips, setEarlyBirdBonusChips] = useState("0");

  // Dealer Appreciation
  const [dealerAppreciationEnabled, setDealerAppreciationEnabled] = useState(false);
  const [dealerAppreciationCost, setDealerAppreciationCost] = useState("20");
  const [dealerAppreciationBonusChips, setDealerAppreciationBonusChips] =
    useState("1000");
  const [dealerAppreciationPrizePoolAmount, setDealerAppreciationPrizePoolAmount] =
    useState("10");
  const [dealerAppreciationDealerAmount, setDealerAppreciationDealerAmount] =
    useState("10");

  // Email settings
  const [sendAnnouncementEmailNow, setSendAnnouncementEmailNow] = useState(false);
  const [sendEarlyBirdReminderEmail, setSendEarlyBirdReminderEmail] = useState(false);
  const [sendTournamentReminderEmail, setSendTournamentReminderEmail] = useState(false);

  // SMS settings
  const [allowSmsRegistration, setAllowSmsRegistration] = useState(true);
  const [sendAnnouncementTextNow, setSendAnnouncementTextNow] = useState(false);
  const [sendEarlyBirdReminderText, setSendEarlyBirdReminderText] = useState(false);
  const [sendTournamentReminderText, setSendTournamentReminderText] = useState(false);

  // Templates
  const [blindTemplateId, setBlindTemplateId] = useState("");
  const [chipTemplateId, setChipTemplateId] = useState("");

  const [creating, setCreating] = useState(false);

  const selectedBlindTemplate = useMemo(
    () => blindTemplates.find((t) => t.id === blindTemplateId) || null,
    [blindTemplates, blindTemplateId]
  );

  const selectedChipTemplate = useMemo(
    () => chipTemplates.find((t) => t.id === chipTemplateId) || null,
    [chipTemplates, chipTemplateId]
  );

  const levelItems = useMemo(
    () => levelItemsFromTemplate(selectedBlindTemplate),
    [selectedBlindTemplate]
  );

  useEffect(() => {
    const run = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate("/login");
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        const admin = userSnap.exists() && userSnap.data()?.role === "admin";
        setIsAdmin(admin);
        setChecking(false);

        if (!admin) {
          navigate("/dashboard");
          return;
        }

        setLoadingTemplates(true);

        const bsRef = collection(db, "blindStructures");
        const bsQ = query(bsRef, orderBy("name", "asc"));
        const bsSnap = await getDocs(bsQ);
        const bsList = bsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setBlindTemplates(bsList);

        const csRef = collection(db, "chipSets");
        const csQ = query(csRef, orderBy("name", "asc"));
        const csSnap = await getDocs(csQ);
        const csList = csSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setChipTemplates(csList);

        if (bsList.length && !blindTemplateId) setBlindTemplateId(bsList[0].id);
        if (csList.length && !chipTemplateId) setChipTemplateId(csList[0].id);

        setLoadingTemplates(false);
      } catch (err) {
        console.error("CreateTournament init failed:", err);
        setLoadingTemplates(false);
        setChecking(false);
        alert("Could not load templates (permissions?).");
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;

    const nm = String(name || "").trim();
    if (!nm) return alert("Tournament name is required.");

    const loc = String(location || "").trim();
    if (!loc) return alert("Tournament location is required.");

    const buy = Number(buyIn || 0);
    if (buy < 0) return alert("Buy-in must be 0 or greater.");

    const nextTournamentType = tournamentType === "bounty" ? "bounty" : "standard";
    const nextBountyAmount =
      nextTournamentType === "bounty" ? Number(bountyAmount || 0) : 0;
    const nextPrizePoolContribution =
      nextTournamentType === "bounty"
        ? Number(prizePoolContribution || 0)
        : buy;

    if (nextTournamentType === "bounty") {
      if (nextBountyAmount <= 0) {
        return alert("Bounty amount must be greater than 0.");
      }
      if (nextPrizePoolContribution < 0) {
        return alert("Prize pool contribution cannot be negative.");
      }
      if (Math.abs(nextPrizePoolContribution + nextBountyAmount - buy) > 0.001) {
        return alert(
          "For a bounty tournament, prize pool contribution + bounty must equal the buy-in."
        );
      }
    }

    const stack = Number(startingStack || 0);
    if (!stack || stack <= 0) return alert("Starting stack must be greater than 0.");

    const dateTs = toTimestampFromLocalDatetime(dateTime);
    if (!dateTs) return alert("Please choose a valid date/time.");

    const blinds = Array.isArray(selectedBlindTemplate?.blinds)
      ? selectedBlindTemplate.blinds
      : [];

    const chips = Array.isArray(selectedChipTemplate?.chips)
      ? selectedChipTemplate.chips
      : [];

    if (blinds.length === 0) {
      return alert("Please choose a blind structure template.");
    }

    if (chips.length === 0) {
      return alert("Please choose a chip set template.");
    }

    const nextRebuysEnabled = !!rebuysEnabled;
    const nextRebuyCutoffLevel = nextRebuysEnabled ? Number(rebuyCutoffLevel || 0) : 0;
    const nextRebuyPrice = Number(rebuyPrice === "" ? buy : rebuyPrice);
    const nextRebuyPrizeContribution = nextTournamentType === "bounty"
      ? Number(rebuyPrizePoolContribution === "" ? nextPrizePoolContribution : rebuyPrizePoolContribution)
      : nextRebuyPrice;
    const nextRebuyBounty = nextTournamentType === "bounty"
      ? Number(rebuyBountyAmount === "" ? nextBountyAmount : rebuyBountyAmount)
      : 0;

    if (nextRebuysEnabled) {
      if (!nextRebuyCutoffLevel || nextRebuyCutoffLevel <= 0) {
        return alert("Choose a rebuy cutoff level.");
      }
      const validLevel = levelItems.some((it) => Number(it.level) === nextRebuyCutoffLevel);
      if (!validLevel) {
        return alert("Rebuy cutoff level is not valid for the selected blind structure.");
      }
      if (!Number.isFinite(nextRebuyPrice) || nextRebuyPrice < 0 ||
          (nextTournamentType === "bounty" && nextRebuyPrice <= 0)) {
        return alert("Enter a valid rebuy price.");
      }
      if (nextTournamentType === "bounty") {
        if (!Number.isFinite(nextRebuyPrizeContribution) || nextRebuyPrizeContribution < 0 ||
            !Number.isFinite(nextRebuyBounty) || nextRebuyBounty <= 0 ||
            Math.abs(nextRebuyPrizeContribution + nextRebuyBounty - nextRebuyPrice) > 0.001) {
          return alert("Rebuy prize pool contribution + rebuy bounty must equal rebuy price.");
        }
      }
    }

    const nextLateRegistrationEnabled = !!lateRegistrationEnabled;
    const nextLateRegistrationCutoffLevel = nextLateRegistrationEnabled
      ? Number(lateRegistrationCutoffLevel || 0)
      : 0;

    if (nextLateRegistrationEnabled) {
      if (!nextLateRegistrationCutoffLevel || nextLateRegistrationCutoffLevel <= 0) {
        return alert("Choose a late registration cutoff level.");
      }

      const validLevel = levelItems.some(
        (it) => Number(it.level) === nextLateRegistrationCutoffLevel
      );

      if (!validLevel) {
        return alert(
          "Late registration cutoff level is not valid for the selected blind structure."
        );
      }
    }

    const nextAddOnsEnabled = !!addOnsEnabled;
    const nextAddOnLevel = nextAddOnsEnabled ? Number(addOnLevel || 0) : 0;
    const nextAddOnChips = nextAddOnsEnabled ? Number(addOnChips || 0) : 0;
    const nextAddOnCost = nextAddOnsEnabled ? Number(addOnCost || 0) : 0;

    if (nextAddOnsEnabled) {
      if (!nextAddOnLevel || nextAddOnLevel <= 0) {
        return alert("Choose the level for add-ons.");
      }
      const validLevel = levelItems.some((it) => Number(it.level) === nextAddOnLevel);
      if (!validLevel) {
        return alert("Add-on level is not valid for the selected blind structure.");
      }
      if (nextAddOnChips <= 0) {
        return alert("Add-on chips must be greater than 0.");
      }
      if (nextAddOnCost < 0) {
        return alert("Add-on cost must be 0 or greater.");
      }
    }

    const nextEarlyBirdEnabled = !!earlyBirdEnabled;
    const nextEarlyBirdDeadlineTs = nextEarlyBirdEnabled
      ? toTimestampFromLocalDatetime(earlyBirdDeadline)
      : null;
    const nextEarlyBirdBonusChips = nextEarlyBirdEnabled
      ? Number(earlyBirdBonusChips || 0)
      : 0;

    if (nextEarlyBirdEnabled) {
      if (!nextEarlyBirdDeadlineTs) {
        return alert("Choose a valid early bird deadline.");
      }
      if (nextEarlyBirdDeadlineTs.toMillis() >= dateTs.toMillis()) {
        return alert("Early bird deadline must be before the tournament date/time.");
      }
      if (nextEarlyBirdBonusChips <= 0) {
        return alert("Early bird bonus chips must be greater than 0.");
      }
    }

    const nextDealerAppreciationEnabled = !!dealerAppreciationEnabled;
    const nextDealerAppreciationCost = nextDealerAppreciationEnabled
      ? Number(dealerAppreciationCost || 0)
      : 0;
    const nextDealerAppreciationBonusChips = nextDealerAppreciationEnabled
      ? Number(dealerAppreciationBonusChips || 0)
      : 0;
    const nextDealerAppreciationPrizePoolAmount = nextDealerAppreciationEnabled
      ? Number(dealerAppreciationPrizePoolAmount || 0)
      : 0;
    const nextDealerAppreciationDealerAmount = nextDealerAppreciationEnabled
      ? Number(dealerAppreciationDealerAmount || 0)
      : 0;

    if (nextDealerAppreciationEnabled) {
      if (nextDealerAppreciationCost <= 0) {
        return alert("Dealer Appreciation cost must be greater than 0.");
      }

      if (nextDealerAppreciationBonusChips <= 0) {
        return alert("Dealer Appreciation bonus chips must be greater than 0.");
      }

      if (nextDealerAppreciationPrizePoolAmount < 0) {
        return alert("Dealer Appreciation prize pool portion cannot be negative.");
      }

      if (nextDealerAppreciationDealerAmount < 0) {
        return alert("Dealer Appreciation dealer portion cannot be negative.");
      }

      if (
        Math.abs(
          nextDealerAppreciationPrizePoolAmount +
            nextDealerAppreciationDealerAmount -
            nextDealerAppreciationCost
        ) > 0.001
      ) {
        return alert(
          "Dealer Appreciation cost must equal the prize pool portion + dealer portion."
        );
      }
    }

    if (sendEarlyBirdReminderText && !nextEarlyBirdEnabled) {
      return alert("Enable Early Bird before turning on an Early Bird reminder text.");
    }

    if (sendEarlyBirdReminderEmail && !nextEarlyBirdEnabled) {
      return alert("Enable Early Bird before turning on an Early Bird reminder email.");
    }

    setCreating(true);

    try {
      const tRef = collection(db, "tournaments");

      const payload = {
        name: nm,
        location: loc,
        date: dateTs,
        buyIn: buy,
        startingStack: stack,

        // Tournament type / bounty accounting
        tournamentType: nextTournamentType,
        bountyEnabled: nextTournamentType === "bounty",
        bountyAmount: nextTournamentType === "bounty" ? nextBountyAmount : 0,
        prizePoolContribution:
          nextTournamentType === "bounty" ? nextPrizePoolContribution : buy,

        // Live / member-facing control
        communicationsLive: !!communicationsLive,

        // Tournament format
        rebuysEnabled: nextRebuysEnabled,
        rebuyCutoffLevel: nextRebuysEnabled ? nextRebuyCutoffLevel : null,
        rebuyPrice: nextRebuysEnabled ? nextRebuyPrice : null,
        rebuyPrizePoolContribution: nextRebuysEnabled ? nextRebuyPrizeContribution : null,
        rebuyBountyAmount: nextRebuysEnabled ? nextRebuyBounty : null,

        lateRegistrationEnabled: nextLateRegistrationEnabled,
        lateRegistrationCutoffLevel: nextLateRegistrationEnabled
          ? nextLateRegistrationCutoffLevel
          : null,
        lateRegistrationOpen: false,
        lateRegistrationClosedAt: null,
        lateRegistrationClosedByUid: null,
        lateRegistrationClosedByName: null,

        addOnsEnabled: nextAddOnsEnabled,
        addOnLevel: nextAddOnsEnabled ? nextAddOnLevel : null,
        addOnChips: nextAddOnsEnabled ? nextAddOnChips : 0,
        addOnCost: nextAddOnsEnabled ? nextAddOnCost : 0,

        earlyBirdEnabled: nextEarlyBirdEnabled,
        earlyBirdDeadline: nextEarlyBirdEnabled ? nextEarlyBirdDeadlineTs : null,
        earlyBirdBonusChips: nextEarlyBirdEnabled ? nextEarlyBirdBonusChips : 0,

        // Dealer Appreciation
        dealerAppreciationEnabled: nextDealerAppreciationEnabled,
        dealerAppreciationCost: nextDealerAppreciationEnabled
          ? nextDealerAppreciationCost
          : 0,
        dealerAppreciationBonusChips: nextDealerAppreciationEnabled
          ? nextDealerAppreciationBonusChips
          : 0,
        dealerAppreciationPrizePoolAmount: nextDealerAppreciationEnabled
          ? nextDealerAppreciationPrizePoolAmount
          : 0,
        dealerAppreciationDealerAmount: nextDealerAppreciationEnabled
          ? nextDealerAppreciationDealerAmount
          : 0,

        // Email settings
        sendAnnouncementEmailNow: !!sendAnnouncementEmailNow,
        sendEarlyBirdReminderEmail: !!sendEarlyBirdReminderEmail,
        sendTournamentReminderEmail: !!sendTournamentReminderEmail,

        // SMS settings
        sendAnnouncementTextNow: !!sendAnnouncementTextNow,
        sendEarlyBirdReminderText: !!sendEarlyBirdReminderText,
        sendTournamentReminderText: !!sendTournamentReminderText,
        allowSmsRegistration: !!allowSmsRegistration,

        status: "registering",
        entrantsCount: 0,
        playersRemaining: 0,

        totalRebuys: 0,
        totalAddOns: 0,

        blinds,
        chips,

        blindTemplateId: blindTemplateId || null,
        blindTemplateName: selectedBlindTemplate?.name || null,
        chipTemplateId: chipTemplateId || null,
        chipTemplateName: selectedChipTemplate?.name || null,

        clock: {
          status: "stopped",
          index: 0,
          startedAt: null,
          endsAt: null,
          pausedAt: null,
          remainingMs: null,
        },

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const created = await addDoc(tRef, payload);

      alert("Tournament created.");
      navigate(`/tournament/${created.id}`);
    } catch (err) {
      console.error("Create tournament failed:", err);
      alert("Could not create tournament. Check Firestore rules & admin role.");
    }

    setCreating(false);
  };

  if (checking) {
    return (
      <Layout>
        <p className="text-zinc-300">Checking access...</p>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h2 className="text-3xl font-bold" style={{ color: COPPER }}>
              Create Tournament
            </h2>
            <p className="text-zinc-400 mt-1">
              Set the tournament format up front so the created tournament is ready to run.
            </p>
          </div>

          <button
            onClick={() => navigate("/dashboard")}
            className="px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold"
          >
            Back
          </button>
        </div>

        <form
          onSubmit={handleCreate}
          className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-6"
        >
          <div>
            <label className="block text-zinc-300 text-sm mb-1">Tournament Name</label>
            <input
              className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
              placeholder="e.g. Friday Night Turbo"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-zinc-300 text-sm mb-1">Location</label>
            <input
              className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
              placeholder="e.g. River Ranch Poker Club, La Grange TX"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              required
            />
            <p className="text-zinc-500 text-xs mt-1">
              This will be used in tournament details and future email/SMS communications.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-zinc-300 text-sm mb-1">Date & Time</label>
              <input
                type="datetime-local"
                className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                value={dateTime}
                onChange={(e) => setDateTime(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-zinc-300 text-sm mb-1">Buy-in (USD)</label>
              <input
                type="number"
                className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                value={buyIn}
                onChange={(e) => setBuyIn(e.target.value)}
                min="0"
                step="1"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-zinc-300 text-sm mb-1">Starting Stack</label>
              <input
                type="number"
                className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                value={startingStack}
                onChange={(e) => setStartingStack(e.target.value)}
                min="1"
                step="1"
              />
            </div>

            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
              <p className="text-zinc-100 font-semibold">Templates Required</p>
              <p className="text-zinc-400 text-sm mt-2">
                This tournament will copy its blind structure and chip set from your saved templates.
              </p>
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-5">
            <h3 className="text-xl font-semibold mb-3" style={{ color: COPPER }}>
              Tournament Format
            </h3>

            <div className="grid md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => {
                  setTournamentType("standard");
                  setPrizePoolContribution(buyIn);
                }}
                className={`text-left rounded-xl border p-4 transition ${
                  tournamentType === "standard"
                    ? "border-[#B87333] bg-[#B87333]/10"
                    : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"
                }`}
              >
                <div className="text-zinc-100 font-semibold">Standard NLHE</div>
                <div className="text-zinc-500 text-sm mt-1">
                  The full buy-in contributes to the regular prize pool.
                </div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setTournamentType("bounty");
                  const buy = Math.max(0, Number(buyIn || 0));
                  const defaultBounty = Math.min(10, buy);
                  setBountyAmount(String(defaultBounty));
                  setPrizePoolContribution(String(Math.max(0, buy - defaultBounty)));
                }}
                className={`text-left rounded-xl border p-4 transition ${
                  tournamentType === "bounty"
                    ? "border-[#B87333] bg-[#B87333]/10"
                    : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"
                }`}
              >
                <div className="text-zinc-100 font-semibold">♠ Bounty NLHE</div>
                <div className="text-zinc-500 text-sm mt-1">
                  Every tournament life carries a fixed bounty.
                </div>
              </button>
            </div>

            {tournamentType === "bounty" ? (
              <div className="mt-4 grid md:grid-cols-3 gap-4 rounded-xl border border-[#B87333]/30 bg-[#B87333]/5 p-4">
                <div>
                  <label className="block text-zinc-300 text-sm mb-1">
                    Prize Pool / Entry
                  </label>
                  <input
                    type="number"
                    className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                    value={prizePoolContribution}
                    onChange={(e) => setPrizePoolContribution(e.target.value)}
                    min="0"
                    step="1"
                  />
                </div>

                <div>
                  <label className="block text-zinc-300 text-sm mb-1">
                    Bounty / Life
                  </label>
                  <input
                    type="number"
                    className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                    value={bountyAmount}
                    onChange={(e) => setBountyAmount(e.target.value)}
                    min="1"
                    step="1"
                  />
                </div>

                <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4">
                  <div className="text-xs uppercase tracking-wide text-zinc-500">
                    Buy-In Check
                  </div>
                  <div className="mt-1 text-lg font-semibold text-zinc-100">
                    ${Number(prizePoolContribution || 0).toFixed(2)} + $
                    {Number(bountyAmount || 0).toFixed(2)}
                  </div>
                  <div
                    className={`text-xs mt-1 ${
                      Math.abs(
                        Number(prizePoolContribution || 0) +
                          Number(bountyAmount || 0) -
                          Number(buyIn || 0)
                      ) < 0.001
                        ? "text-green-400"
                        : "text-red-400"
                    }`}
                  >
                    {Math.abs(
                      Number(prizePoolContribution || 0) +
                        Number(bountyAmount || 0) -
                        Number(buyIn || 0)
                    ) < 0.001
                      ? "Matches buy-in"
                      : "Must equal buy-in"}
                  </div>
                </div>

                <div className="md:col-span-3 text-xs text-zinc-500">
                  Rebuys create a new tournament life with a new bounty. Add-ons do not create
                  another bounty.
                </div>
              </div>
            ) : null}
          </div>

          <div className="border-t border-zinc-800 pt-5">
            <h3 className="text-xl font-semibold mb-3" style={{ color: COPPER }}>
              Go Live for Members
            </h3>

            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 accent-[#B87333]"
                  checked={communicationsLive}
                  onChange={(e) => setCommunicationsLive(e.target.checked)}
                />
                <div>
                  <p className="text-zinc-100 font-semibold">
                    Visible to members + communications live
                  </p>
                  <p className="text-zinc-400 text-sm mt-1">
                    When off, this tournament stays hidden from member Dashboard and all
                    tournament emails/texts are blocked.
                  </p>
                  <p className="text-zinc-500 text-xs mt-2">
                    Recommended for test tournaments: leave this off until you are ready.
                  </p>
                </div>
              </label>
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-5">
            <h3 className="text-xl font-semibold mb-3" style={{ color: COPPER }}>
              Templates
            </h3>

            {loadingTemplates ? (
              <p className="text-zinc-400">Loading templates...</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-300 text-sm mb-1">
                    Blind Structure Template
                  </label>
                  <select
                    className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                    value={blindTemplateId}
                    onChange={(e) => {
                      setBlindTemplateId(e.target.value);
                      setRebuyCutoffLevel("");
                      setAddOnLevel("");
                    }}
                  >
                    {blindTemplates.length === 0 ? (
                      <option value="">No blind structures found</option>
                    ) : (
                      blindTemplates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))
                    )}
                  </select>

                  <p className="text-zinc-500 text-xs mt-2">
                    Levels:{" "}
                    <span className="text-zinc-300 font-semibold">
                      {Array.isArray(selectedBlindTemplate?.blinds)
                        ? selectedBlindTemplate.blinds.length
                        : 0}
                    </span>
                  </p>
                </div>

                <div>
                  <label className="block text-zinc-300 text-sm mb-1">
                    Chip Set Template
                  </label>
                  <select
                    className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                    value={chipTemplateId}
                    onChange={(e) => setChipTemplateId(e.target.value)}
                  >
                    {chipTemplates.length === 0 ? (
                      <option value="">No chip sets found</option>
                    ) : (
                      chipTemplates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))
                    )}
                  </select>

                  <p className="text-zinc-500 text-xs mt-2">
                    Chip types:{" "}
                    <span className="text-zinc-300 font-semibold">
                      {Array.isArray(selectedChipTemplate?.chips)
                        ? selectedChipTemplate.chips.length
                        : 0}
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-zinc-800 pt-5">
            <h3 className="text-xl font-semibold mb-3" style={{ color: COPPER }}>
              Rebuys
            </h3>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
                <div className="flex items-center justify-between gap-4">
                  <p className="text-zinc-100 font-semibold">Enable Rebuys</p>

                  <label className="inline-flex items-center gap-2 select-none">
                    <input
                      type="checkbox"
                      className="h-5 w-5 accent-[#B87333]"
                      checked={rebuysEnabled}
                      onChange={(e) => setRebuysEnabled(e.target.checked)}
                    />
                    <span
                      className={`text-sm font-semibold ${
                        rebuysEnabled ? "text-green-400" : "text-zinc-400"
                      }`}
                    >
                      {rebuysEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </label>
                </div>

                <div className="mt-4">
                  {rebuysEnabled ? (
                    <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-green-500/10 text-green-300 border border-green-500/20">
                      Rebuy Tournament
                    </span>
                  ) : (
                    <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700">
                      Freezeout
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-zinc-300 text-sm mb-1">Rebuy Cutoff Level</label>
                <select
                  className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                  value={rebuyCutoffLevel}
                  onChange={(e) => setRebuyCutoffLevel(e.target.value)}
                  disabled={!rebuysEnabled}
                >
                  <option value="">Select level</option>
                  {levelItems.map((it) => (
                    <option key={it.level} value={it.level}>
                      Level {it.level}
                    </option>
                  ))}
                </select>
                <p className="text-zinc-500 text-xs mt-1">
                  Rebuy price is independently configurable. In a bounty tournament, the configured rebuy bounty is attached to each new life.
                </p>
              </div>
            </div>
            {rebuysEnabled ? (
              <div className="mt-4 grid gap-4 md:grid-cols-3">
                <div>
                  <label className="block text-zinc-300 text-sm mb-1">Rebuy Price (USD)</label>
                  <input type="number" min="0" step="1" className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                    value={rebuyPrice === "" ? buyIn : rebuyPrice}
                    onChange={(e) => setRebuyPrice(e.target.value)} />
                </div>
                {tournamentType === "bounty" ? (
                  <>
                    <div>
                      <label className="block text-zinc-300 text-sm mb-1">Rebuy Prize Pool / Life (USD)</label>
                      <input type="number" min="0" step="1" className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                        value={rebuyPrizePoolContribution === "" ? prizePoolContribution : rebuyPrizePoolContribution}
                        onChange={(e) => setRebuyPrizePoolContribution(e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-zinc-300 text-sm mb-1">Rebuy Bounty / Life (USD)</label>
                      <input type="number" min="0" step="1" className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                        value={rebuyBountyAmount === "" ? bountyAmount : rebuyBountyAmount}
                        onChange={(e) => setRebuyBountyAmount(e.target.value)} />
                    </div>
                    <p className="md:col-span-3 text-xs text-zinc-500">
                      Rebuy prize pool contribution + rebuy bounty must equal the independently set rebuy price.
                    </p>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="border-t border-zinc-800 pt-5">
            <h3 className="text-xl font-semibold mb-3" style={{ color: COPPER }}>
              Late Registration
            </h3>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-zinc-100 font-semibold">Enable Late Registration</p>
                    <p className="text-zinc-500 text-xs mt-1">
                      Allows new first-time entrants after the tournament starts.
                    </p>
                  </div>

                  <label className="inline-flex items-center gap-2 select-none">
                    <input
                      type="checkbox"
                      className="h-5 w-5 accent-[#B87333]"
                      checked={lateRegistrationEnabled}
                      onChange={(e) => setLateRegistrationEnabled(e.target.checked)}
                    />
                    <span
                      className={`text-sm font-semibold ${
                        lateRegistrationEnabled ? "text-green-400" : "text-zinc-400"
                      }`}
                    >
                      {lateRegistrationEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-zinc-300 text-sm mb-1">
                  Late Registration Cutoff Level
                </label>
                <select
                  className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                  value={lateRegistrationCutoffLevel}
                  onChange={(e) => setLateRegistrationCutoffLevel(e.target.value)}
                  disabled={!lateRegistrationEnabled}
                >
                  <option value="">Select level</option>
                  {levelItems.map((it) => (
                    <option key={it.level} value={it.level}>
                      End of Level {it.level}
                    </option>
                  ))}
                </select>
                <p className="text-zinc-500 text-xs mt-1">
                  Example: selecting Level 2 keeps late registration open through Level 2
                  and closes it when play advances beyond that level.
                </p>
              </div>
            </div>

            {lateRegistrationEnabled ? (
              <div className="mt-4 rounded-lg border border-blue-500/20 bg-blue-500/10 p-3 text-sm text-blue-200">
                Late entrants receive the normal starting stack, do not receive Early Bird
                chips, count as a new paid entry, and receive a new Life 1 bounty when this
                is a bounty tournament.
              </div>
            ) : null}
          </div>

          <div className="border-t border-zinc-800 pt-5">
            <h3 className="text-xl font-semibold mb-3" style={{ color: COPPER }}>
              Add-Ons
            </h3>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
                <div className="flex items-center justify-between gap-4">
                  <p className="text-zinc-100 font-semibold">Enable Add-Ons</p>

                  <label className="inline-flex items-center gap-2 select-none">
                    <input
                      type="checkbox"
                      className="h-5 w-5 accent-[#B87333]"
                      checked={addOnsEnabled}
                      onChange={(e) => setAddOnsEnabled(e.target.checked)}
                    />
                    <span
                      className={`text-sm font-semibold ${
                        addOnsEnabled ? "text-green-400" : "text-zinc-400"
                      }`}
                    >
                      {addOnsEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-zinc-300 text-sm mb-1">Add-On Level</label>
                <select
                  className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                  value={addOnLevel}
                  onChange={(e) => setAddOnLevel(e.target.value)}
                  disabled={!addOnsEnabled}
                >
                  <option value="">Select level</option>
                  {levelItems.map((it) => (
                    <option key={it.level} value={it.level}>
                      Level {it.level}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-zinc-300 text-sm mb-1">Add-On Bonus Chips</label>
                <input
                  type="number"
                  className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                  value={addOnChips}
                  onChange={(e) => setAddOnChips(e.target.value)}
                  min="0"
                  step="1"
                  disabled={!addOnsEnabled}
                />
              </div>

              <div>
                <label className="block text-zinc-300 text-sm mb-1">Add-On Cost (USD)</label>
                <input
                  type="number"
                  className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                  value={addOnCost}
                  onChange={(e) => setAddOnCost(e.target.value)}
                  min="0"
                  step="1"
                  disabled={!addOnsEnabled}
                />
              </div>
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-5">
            <h3 className="text-xl font-semibold mb-3" style={{ color: COPPER }}>
              Early Bird Registration
            </h3>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
                <div className="flex items-center justify-between gap-4">
                  <p className="text-zinc-100 font-semibold">Enable Early Bird</p>

                  <label className="inline-flex items-center gap-2 select-none">
                    <input
                      type="checkbox"
                      className="h-5 w-5 accent-[#B87333]"
                      checked={earlyBirdEnabled}
                      onChange={(e) => setEarlyBirdEnabled(e.target.checked)}
                    />
                    <span
                      className={`text-sm font-semibold ${
                        earlyBirdEnabled ? "text-green-400" : "text-zinc-400"
                      }`}
                    >
                      {earlyBirdEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-zinc-300 text-sm mb-1">
                  Early Bird Deadline
                </label>
                <input
                  type="datetime-local"
                  className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                  value={earlyBirdDeadline}
                  onChange={(e) => setEarlyBirdDeadline(e.target.value)}
                  disabled={!earlyBirdEnabled}
                />
              </div>
            </div>

            <div>
              <label className="block text-zinc-300 text-sm mb-1">
                Early Bird Bonus Chips
              </label>
              <input
                type="number"
                className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                value={earlyBirdBonusChips}
                onChange={(e) => setEarlyBirdBonusChips(e.target.value)}
                min="0"
                step="1"
                disabled={!earlyBirdEnabled}
              />
              <p className="text-zinc-500 text-xs mt-1">
                Example: register before the deadline and receive an extra 250 chips.
              </p>
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-5">
            <h3 className="text-xl font-semibold mb-3" style={{ color: COPPER }}>
              Dealer Appreciation
            </h3>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-zinc-100 font-semibold">
                      Enable Dealer Appreciation
                    </p>
                    <p className="text-zinc-500 text-xs mt-1">
                      Optional extra contribution for bonus tournament chips.
                    </p>
                  </div>

                  <label className="inline-flex items-center gap-2 select-none">
                    <input
                      type="checkbox"
                      className="h-5 w-5 accent-[#B87333]"
                      checked={dealerAppreciationEnabled}
                      onChange={(e) => setDealerAppreciationEnabled(e.target.checked)}
                    />
                    <span
                      className={`text-sm font-semibold ${
                        dealerAppreciationEnabled ? "text-green-400" : "text-zinc-400"
                      }`}
                    >
                      {dealerAppreciationEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </label>
                </div>
              </div>

              <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
                <p className="text-zinc-100 font-semibold">RRPC Example</p>
                <p className="text-zinc-400 text-sm mt-2">
                  $20 Dealer Appreciation → +1,000 chips
                </p>
                <p className="text-zinc-500 text-xs mt-1">
                  $10 to regular prize pool • $10 to dealers
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-zinc-300 text-sm mb-1">
                  Dealer Appreciation Cost (USD)
                </label>
                <input
                  type="number"
                  className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                  value={dealerAppreciationCost}
                  onChange={(e) => setDealerAppreciationCost(e.target.value)}
                  min="0"
                  step="1"
                  disabled={!dealerAppreciationEnabled}
                />
              </div>

              <div>
                <label className="block text-zinc-300 text-sm mb-1">
                  Bonus Chips
                </label>
                <input
                  type="number"
                  className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                  value={dealerAppreciationBonusChips}
                  onChange={(e) => setDealerAppreciationBonusChips(e.target.value)}
                  min="0"
                  step="1"
                  disabled={!dealerAppreciationEnabled}
                />
              </div>

              <div>
                <label className="block text-zinc-300 text-sm mb-1">
                  Prize Pool Portion (USD)
                </label>
                <input
                  type="number"
                  className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                  value={dealerAppreciationPrizePoolAmount}
                  onChange={(e) => setDealerAppreciationPrizePoolAmount(e.target.value)}
                  min="0"
                  step="1"
                  disabled={!dealerAppreciationEnabled}
                />
              </div>

              <div>
                <label className="block text-zinc-300 text-sm mb-1">
                  Dealer Portion (USD)
                </label>
                <input
                  type="number"
                  className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                  value={dealerAppreciationDealerAmount}
                  onChange={(e) => setDealerAppreciationDealerAmount(e.target.value)}
                  min="0"
                  step="1"
                  disabled={!dealerAppreciationEnabled}
                />
              </div>
            </div>

            {dealerAppreciationEnabled ? (
              <div className="mt-4 rounded-lg border border-[#B87333]/30 bg-[#B87333]/5 p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-zinc-100 font-semibold">Split Check</p>
                    <p className="text-zinc-500 text-xs mt-1">
                      Prize pool portion + dealer portion must equal the total cost.
                    </p>
                  </div>

                  <div className="text-right">
                    <div className="text-zinc-100 font-semibold">
                      ${Number(dealerAppreciationPrizePoolAmount || 0).toFixed(2)} + $
                      {Number(dealerAppreciationDealerAmount || 0).toFixed(2)} = $
                      {(
                        Number(dealerAppreciationPrizePoolAmount || 0) +
                        Number(dealerAppreciationDealerAmount || 0)
                      ).toFixed(2)}
                    </div>
                    <div
                      className={`text-xs mt-1 ${
                        Math.abs(
                          Number(dealerAppreciationPrizePoolAmount || 0) +
                            Number(dealerAppreciationDealerAmount || 0) -
                            Number(dealerAppreciationCost || 0)
                        ) < 0.001
                          ? "text-green-400"
                          : "text-red-400"
                      }`}
                    >
                      {Math.abs(
                        Number(dealerAppreciationPrizePoolAmount || 0) +
                          Number(dealerAppreciationDealerAmount || 0) -
                          Number(dealerAppreciationCost || 0)
                      ) < 0.001
                        ? "Matches Dealer Appreciation cost"
                        : "Split must equal Dealer Appreciation cost"}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div className="border-t border-zinc-800 pt-5">
            <h3 className="text-xl font-semibold mb-3" style={{ color: COPPER }}>
              Email Notifications
            </h3>

            <div className="grid gap-3">
              <label className="flex items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-4 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 accent-[#B87333]"
                  checked={sendAnnouncementEmailNow}
                  onChange={(e) => setSendAnnouncementEmailNow(e.target.checked)}
                />
                <div>
                  <p className="text-zinc-100 font-semibold">
                    Send tournament announcement email now
                  </p>
                  <p className="text-zinc-400 text-sm mt-1">
                    Sends the initial tournament announcement email to all active users once
                    this tournament is live for members.
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-4 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 accent-[#B87333]"
                  checked={sendEarlyBirdReminderEmail}
                  onChange={(e) => setSendEarlyBirdReminderEmail(e.target.checked)}
                  disabled={!earlyBirdEnabled}
                />
                <div>
                  <p className="text-zinc-100 font-semibold">
                    Send Early Bird reminder email
                  </p>
                  <p className="text-zinc-400 text-sm mt-1">
                    Sends an automatic reminder before the Early Bird window closes.
                  </p>
                  {!earlyBirdEnabled ? (
                    <p className="text-zinc-500 text-xs mt-1">
                      Enable Early Bird to use this reminder.
                    </p>
                  ) : null}
                </div>
              </label>

              <label className="flex items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-4 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 accent-[#B87333]"
                  checked={sendTournamentReminderEmail}
                  onChange={(e) => setSendTournamentReminderEmail(e.target.checked)}
                />
                <div>
                  <p className="text-zinc-100 font-semibold">
                    Send tournament reminder email
                  </p>
                  <p className="text-zinc-400 text-sm mt-1">
                    Sends an automatic reminder before the tournament start time.
                  </p>
                </div>
              </label>
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-5">
            <h3 className="text-xl font-semibold mb-3" style={{ color: COPPER }}>
              SMS Registration
            </h3>

            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 accent-[#B87333]"
                  checked={allowSmsRegistration}
                  onChange={(e) => setAllowSmsRegistration(e.target.checked)}
                />
                <div>
                  <p className="text-zinc-100 font-semibold">Allow SMS registration by reply</p>
                  <p className="text-zinc-400 text-sm mt-1">
                    When enabled, members can reply{" "}
                    <span className="font-semibold text-zinc-200">REGISTER</span> to
                    tournament announcement texts and be registered automatically once the
                    tournament is live for members.
                  </p>
                  <p className="text-zinc-500 text-xs mt-2">
                    Default: enabled for new tournaments.
                  </p>
                </div>
              </label>
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-5">
            <h3 className="text-xl font-semibold mb-3" style={{ color: COPPER }}>
              SMS Notifications
            </h3>

            <div className="grid gap-3">
              <label className="flex items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-4 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 accent-[#B87333]"
                  checked={sendAnnouncementTextNow}
                  onChange={(e) => setSendAnnouncementTextNow(e.target.checked)}
                />
                <div>
                  <p className="text-zinc-100 font-semibold">
                    Send tournament announcement text now
                  </p>
                  <p className="text-zinc-400 text-sm mt-1">
                    Sends the initial tournament announcement text to eligible opted-in members
                    once this tournament is live for members.
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-4 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 accent-[#B87333]"
                  checked={sendEarlyBirdReminderText}
                  onChange={(e) => setSendEarlyBirdReminderText(e.target.checked)}
                  disabled={!earlyBirdEnabled}
                />
                <div>
                  <p className="text-zinc-100 font-semibold">
                    Send Early Bird reminder text
                  </p>
                  <p className="text-zinc-400 text-sm mt-1">
                    Sends an automatic reminder before the Early Bird window closes.
                  </p>
                  {!earlyBirdEnabled ? (
                    <p className="text-zinc-500 text-xs mt-1">
                      Enable Early Bird to use this reminder.
                    </p>
                  ) : null}
                </div>
              </label>

              <label className="flex items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-4 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 accent-[#B87333]"
                  checked={sendTournamentReminderText}
                  onChange={(e) => setSendTournamentReminderText(e.target.checked)}
                />
                <div>
                  <p className="text-zinc-100 font-semibold">
                    Send tournament reminder text
                  </p>
                  <p className="text-zinc-400 text-sm mt-1">
                    Sends a reminder text before the tournament start time.
                  </p>
                </div>
              </label>
            </div>
          </div>

          <div className="pt-2 flex flex-wrap gap-3 justify-end">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="px-4 py-3 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={creating}
              className={`px-5 py-3 rounded font-semibold text-zinc-950 ${
                creating
                  ? "bg-[#B87333]/60 cursor-not-allowed"
                  : "bg-[#B87333] hover:bg-[#A46230]"
              }`}
            >
              {creating ? "Creating..." : "Create Tournament"}
            </button>
          </div>

          {(blindTemplates.length === 0 || chipTemplates.length === 0) && !loadingTemplates && (
            <div className="text-zinc-400 text-sm mt-2">
              Heads up: You’re missing{" "}
              {blindTemplates.length === 0 ? "Blind Structures" : ""}{" "}
              {blindTemplates.length === 0 && chipTemplates.length === 0 ? "and" : ""}{" "}
              {chipTemplates.length === 0 ? "Chip Sets" : ""}. Create templates first for the best workflow.
            </div>
          )}
        </form>
      </div>
    </Layout>
  );
}
